import React from 'react';
import { Link } from 'react-router-dom';
import LazyLoad from 'react-lazyload';
import classNames from 'classnames';
import includes from 'lodash/includes';

import { StarRatingDisplay } from '@nexus/fff-ratings-and-reviews';
import { updateLocationSearch } from 'utils/url';
import { formatCurrency } from 'utils/currency';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { VIEW_SIZE } from 'utils/device';
import withSelectedVariant from 'HOC/withSelectedVariant/withSelectedVariant';
import Variants from '../Variants/Variants';
import UpdateCartButton from '../UpdateCartButton/UpdateCartButton';
import FavButton from '../FavButton/FavButton';
import TeamAvatars from '../TeamAvatars/TeamAvatars';
import SubscriptionBadge from '../SubscriptionBadge/SubscriptionBadge';
import SubscribeAndSaveLabel from './SubscribeAndSaveLabel/SubscribeAndSaveLabel';
import styles from './Product.module.css';

const formatVariantDescriptor = (text) => text.replace(/\//g, ' / ');
const formatPrice = (price) => formatCurrency(Number(price));

const ImageWrapper = ({ children, disableLazyLoading }) =>
  disableLazyLoading ? (
    <>{children}</>
  ) : (
    <LazyLoad once className={styles.lazyLoad}>
      {children}
    </LazyLoad>
  );

const Product = (props) => {
  const {
    id,
    brandName,
    name,
    imageUrl,
    selectedVariant,
    selectedDescriptors,
    updateSelectedVariant,
    updateSelectedDescriptors,
    variants,
    viewSize,
    shareUrl,
    isFavorite,
    sponsored,
    sponsoredThreshold,
    subtotal,
    hasSponsoredProductInCart,
    gwpProducts,
    teamCart,
    socialEcommFF,
    style,
    carouselHeightClassName,
    positionLocation,
    disableLazyLoading,
    accordionInGrid,
    buttonLocation,
    referrerProductId,
    positionOrder,
    rating,
    showNoCheckoutRequiredModal,
    loggedIn,
    hasActiveSubscription,
  } = props;

  const imageSrc =
    selectedVariant === null ? imageUrl : selectedVariant.imageUrl;
  const {
    rateAndReview: rateAndReviewFF,
    alternateSubscriptions: alternateSubscriptionsFF,
  } = useFlags();
  //const selectedDescriptors = selectedVariant !== null ? selectedVariant.variantDescriptors : null;
  // Gets the color of the current variant if it has a color variant otherwise returns null
  const selectedVariantColor =
    selectedDescriptors.find((descriptor) => {
      return descriptor.type === 'COLOR';
    }) || null;
  // Checks to see if a color was found, therefore it is a color variant
  const isSelectedColorVariant = selectedVariantColor !== null;
  // Checks to see if color type variant is the only variant descriptor on this variant
  const isColorOnlyVariant =
    isSelectedColorVariant === true && selectedDescriptors.length === 1;
  const isMultipleVariant = variants.length > 1;
  const variantId =
    selectedVariant === null ? variants[0].id : selectedVariant.id;
  const displayTeamAvatars = socialEcommFF && teamCart;
  const displayVariants = isColorOnlyVariant && isMultipleVariant;
  const isPNG = includes(imageSrc, '.png');
  const subscriptionAvailable =
    alternateSubscriptionsFF && selectedVariant?.isSubscriptionAvailable;
  const oneTimePurchaseInCart =
    selectedVariant?.quantity > 0 && !selectedVariant?.cadenceTypeInCart;
  const displaySubscriptionPricing =
    (subscriptionAvailable &&
      !oneTimePurchaseInCart &&
      !hasActiveSubscription) ||
    !!selectedVariant.cadenceTypeInCart;
  // "retailPrice" is the price for subscription products
  // while "price" is the price for one-time purchase
  const retailPrice = displaySubscriptionPricing
    ? priceDisplay('retailPrice')
    : priceDisplay('price');
  const showCrossedPrice = priceDisplay('salePrice') !== retailPrice;
  const percentOff = displaySubscriptionPricing
    ? selectedVariant.subscription.percentOff
    : selectedVariant.percentOff;
  // display low inventory message for one-time purchase products only
  const isLowInventory =
    (!subscriptionAvailable || oneTimePurchaseInCart) &&
    selectedVariant &&
    !selectedVariant.soldOut &&
    selectedVariant.lowInventory;

  function priceDisplay(priceType) {
    let maxPrice = 0;
    let minPrice = Infinity;
    for (const variant of variants) {
      // use "price" instead of "salePrice" if null for one-time purchase
      const priceName =
        priceType === 'salePrice' &&
        variant.salePrice === null &&
        (!subscriptionAvailable || oneTimePurchaseInCart)
          ? 'price'
          : priceType;
      // subscribable product: display retailPrice/salePrice from variant.subscription
      // one-time purchase: display price/salePrice from variant
      const displayedPrice = displaySubscriptionPricing
        ? variant.subscription[priceName]
        : variant[priceName];
      if (parseFloat(displayedPrice) > parseFloat(maxPrice)) {
        maxPrice = displayedPrice;
      }
      if (parseFloat(displayedPrice) < parseFloat(minPrice)) {
        minPrice = displayedPrice;
      }
    }

    if (
      selectedVariant !== null &&
      selectedVariantColor !== null &&
      selectedVariantColor.value.length > 0
    ) {
      const priceName =
        priceType === 'salePrice' &&
        selectedVariant.salePrice === null &&
        !subscriptionAvailable
          ? 'price'
          : priceType;
      const displayedPrice = subscriptionAvailable
        ? selectedVariant.subscription[priceName]
        : selectedVariant[priceName];
      return formatPrice(displayedPrice);
    }
    if (minPrice !== maxPrice) {
      return `${formatPrice(minPrice)}-${formatPrice(maxPrice)}`;
    }
    return formatPrice(maxPrice);
  }

  return (
    <li
      className={classNames(styles.productContainer, carouselHeightClassName)}
      style={style}
      data-testid="product-container"
    >
      {displayTeamAvatars && (
        <TeamAvatars
          list={teamCart}
          className={classNames(styles.teamCart, {
            [styles.discountBanner]: showCrossedPrice,
          })}
        />
      )}
      {subscriptionAvailable && (
        <div className={styles.subscriptionBadge}>
          <SubscriptionBadge />
        </div>
      )}
      <Link
        to={updateLocationSearch(
          {
            product_link: shareUrl,
            ...(referrerProductId && {
              referrer_product_id: referrerProductId,
            }),
            ...(positionOrder && {
              position_order: positionOrder,
            }),
          },
          { accordionInGrid }
        )}
        data-dd-action-name={`${brandName} ${name} accordion link in product card`}
      >
        <span className="sr-only">Click to read product details</span>
        <div className={styles.imageContainer} data-testid="product-image">
          <ImageWrapper disableLazyLoading={disableLazyLoading}>
            <img
              className={classNames(styles.image, {
                [styles.png]: isPNG,
              })}
              alt={`${name}`}
              src={imageSrc}
            />
          </ImageWrapper>
          {isLowInventory && (
            <div className={styles.lowInventory}>Hurry, Low Inventory!</div>
          )}
        </div>
        <div className={styles.textContainer}>
          <div className={styles.header}>
            <div className={styles.nameContainer}>
              <div>
                {brandName && <p className={styles.brandName}>{brandName}</p>}
                <p className={styles.productName} data-testid="product-name">
                  {name}
                </p>
              </div>
              {loggedIn && (
                <FavButton
                  isFavorite={isFavorite}
                  favBtnStyles={{ marginLeft: '12px' }}
                  productId={id}
                  variantId={variantId}
                  buttonLocation={buttonLocation}
                  positionLocation={positionLocation}
                  referrerProductId={referrerProductId}
                  positionOrder={positionOrder}
                />
              )}
            </div>
            <div className={styles.rrContainer}>
              {rateAndReviewFF ? (
                <StarRatingDisplay
                  theme="black"
                  size="small"
                  rating={rating?.averageRating}
                  ratingCount={rating?.totalReviewCount}
                />
              ) : (
                <></>
              )}
            </div>
            <div className={styles.priceContainer}>
              <p className={styles.price} data-testid="product-image-price">
                {priceDisplay('salePrice')}
              </p>
              {showCrossedPrice && (
                <>
                  <span className={styles.crossedPrice}>{retailPrice}</span>
                  <span className={styles.priceOff}>
                    {`(${percentOff}% off)`}
                  </span>
                </>
              )}
            </div>
            {subscriptionAvailable && !hasActiveSubscription && (
              <SubscribeAndSaveLabel
                isOneTimeView={subscriptionAvailable && oneTimePurchaseInCart}
                salePrice={selectedVariant.subscription.salePrice}
                percentOff={selectedVariant.subscription.percentOff}
              />
            )}
          </div>
        </div>
      </Link>
      <div>
        <div
          className={classNames(styles.variants, {
            [styles.selectVariant]: displayVariants,
          })}
        >
          {viewSize !== VIEW_SIZE.small && (
            <>
              {displayVariants && selectedVariant !== null && (
                <p className={styles.descriptor}>
                  {selectedVariantColor !== null &&
                    formatVariantDescriptor(selectedVariantColor.descriptor)}
                </p>
              )}
              {displayVariants && (
                <div className={styles.variantsContainer}>
                  <Variants
                    variants={variants}
                    selectedVariant={selectedVariant}
                    selectedDescriptors={selectedDescriptors}
                    updateSelectedVariant={updateSelectedVariant}
                    updateSelectedDescriptors={updateSelectedDescriptors}
                    context="card"
                  />
                </div>
              )}
            </>
          )}
        </div>
        <div className={styles.buttonContainer}>
          {selectedVariant !== null && (
            <UpdateCartButton
              productName={name}
              nonColorVariant={isMultipleVariant && !isColorOnlyVariant}
              isColorOnlyVariant={isColorOnlyVariant}
              isMultipleVariant={isMultipleVariant}
              shareUrl={shareUrl}
              viewSize={viewSize}
              sponsored={sponsored}
              variant={selectedVariant}
              subtotal={subtotal}
              sponsoredThreshold={sponsoredThreshold}
              buttonLocation={buttonLocation}
              positionLocation={positionLocation}
              positionOrder={positionOrder}
              hasSponsoredProductInCart={hasSponsoredProductInCart}
              gwpProducts={gwpProducts}
              referrerProductId={referrerProductId}
              accordionInGrid={accordionInGrid}
              showNoCheckoutRequiredModal={showNoCheckoutRequiredModal}
              hasActiveSubscription={selectedVariant.hasActiveSubscription}
            />
          )}
        </div>
      </div>
    </li>
  );
};

Product.defaultProps = {
  disableLazyLoading: false,
  accordionInGrid: true,
  referrerProductId: null,
  positionOrder: null,
};

export default withSelectedVariant()(Product);
